import { isSectionSpecPdf } from "@/shared/models/SectionSpecUpload";
import { useObjectUrl } from "@/shared/hooks/useObjectUrl";
import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	IconButton,
	InputAdornment,
	TextField,
	Tooltip,
} from "@mui/material";
import { useState } from "react";

import { useSectionSpecDrawing } from "../../hooks/useSectionSpecDrawing";
import {
	applySectionSpecRotation,
	type SectionSpecFileChange,
	type SectionSpecRotation,
} from "../../models/SectionSpecRotation";
import {
	ROTATION_CONTROLS_HOVER_SX,
	SectionSpecRotationControls,
} from "./SectionSpecRotationControls";

interface SectionSpecImageFieldProps {
	label: string;
	fileId: number | null;
	fileName: string | null;
	placeholder?: string;
	onSaveRotated?: (change: SectionSpecFileChange) => Promise<void>;
}

interface FilePreviewModalProps {
	label: string;
	fileId: number;
	fileName: string | null;
	onClose: () => void;
	onSaveRotated?: (change: SectionSpecFileChange) => Promise<void>;
}

const FilePreviewModal = ({
	label,
	fileId,
	fileName,
	onClose,
	onSaveRotated,
}: FilePreviewModalProps) => {
	const [previewFailed, setPreviewFailed] = useState(false);
	const [change, setChange] = useState<SectionSpecFileChange | null>(null);
	const [rotating, setRotating] = useState(false);
	const [saving, setSaving] = useState(false);
	const [actionError, setActionError] = useState<string | null>(null);

	const originalName = fileName?.split(/[\\/]/).pop() || `file-${fileId}`;
	const downloadName = change?.file.name ?? originalName;
	const {
		data: imageData,
		isError,
		error,
	} = useSectionSpecDrawing(fileId, fileName);
	const displayed = change?.file ?? imageData ?? null;
	const objectUrl = useObjectUrl(displayed);
	const isPdf = isSectionSpecPdf(downloadName, displayed?.type);

	const rotate = async (rotation: SectionSpecRotation) => {
		if (!imageData) return;
		setRotating(true);
		try {
			setChange(
				await applySectionSpecRotation(
					change,
					{ blob: imageData, fileName: originalName },
					rotation,
				),
			);
		} catch (rotateError) {
			setActionError(
				rotateError instanceof Error
					? rotateError.message
					: "The file could not be rotated",
			);
		} finally {
			setRotating(false);
		}
	};

	const saveRotation = async () => {
		if (!change || !onSaveRotated) return;
		setSaving(true);
		try {
			await onSaveRotated(change);
			onClose();
		} catch (saveError) {
			setActionError(
				saveError instanceof Error
					? saveError.message
					: "The rotation could not be saved",
			);
		} finally {
			setSaving(false);
		}
	};

	return (
		<>
			<MesfModal
				open
				handleClose={onClose}
				title={`${label} Preview`}
				maxWidth="lg"
			>
				<MesfModal.Content dividers>
					<Box
						sx={{
							...ROTATION_CONTROLS_HOVER_SX,
							alignItems: "center",
							display: "flex",
							justifyContent: "center",
							minHeight: "30vh",
							position: "relative",
							width: { xs: "70vw", md: "60vw" },
							maxWidth: "100%",
						}}
					>
						{objectUrl && !isError && !previewFailed && (
							<SectionSpecRotationControls
								label={label}
								rotation={{
									canRotate: onSaveRotated !== undefined && imageData != null,
									change,
									rotating,
									saving,
									rotate: (rotation) => void rotate(rotation),
									save: () => void saveRotation(),
									discard: () => setChange(null),
								}}
							/>
						)}
						{isError || previewFailed ? (
							<Alert severity="error">File preview unavailable.</Alert>
						) : objectUrl && isPdf ? (
							<Box
								component="iframe"
								src={`${objectUrl}#toolbar=0`}
								title={`${label} PDF preview`}
								onError={() => setPreviewFailed(true)}
								sx={{ border: 0, height: "65vh", width: "100%" }}
							/>
						) : objectUrl ? (
							<Box
								component="img"
								src={objectUrl}
								alt={`${label} preview`}
								onError={() => setPreviewFailed(true)}
								sx={{
									maxHeight: "65vh",
									maxWidth: "100%",
									objectFit: "contain",
								}}
							/>
						) : (
							<CircularProgress aria-label={`Loading ${label}`} />
						)}
					</Box>
				</MesfModal.Content>
				<MesfModal.Actions>
					<Button onClick={onClose} color="inherit" disabled={saving}>
						Close
					</Button>
					<Button
						component="a"
						href={objectUrl ?? undefined}
						download={downloadName}
						disabled={!objectUrl}
						startIcon={<DownloadIcon />}
						variant="contained"
					>
						Download
					</Button>
				</MesfModal.Actions>
			</MesfModal>
			<ErrorModal error={error?.message ?? null} onHide={onClose} />
			<ErrorModal error={actionError} onHide={() => setActionError(null)} />
		</>
	);
};

export const SectionSpecImageField = ({
	label,
	fileId,
	fileName,
	placeholder = "No persisted file",
	onSaveRotated,
}: SectionSpecImageFieldProps) => {
	const [open, setOpen] = useState(false);
	const hasPersistedFile =
		fileId !== null && Number.isInteger(fileId) && fileId > 0;

	return (
		<>
			<TextField
				disabled
				label={label}
				size="small"
				fullWidth
				value={fileName ?? ""}
				placeholder={placeholder}
				InputProps={{
					endAdornment: hasPersistedFile ? (
						<InputAdornment position="end">
							<Tooltip title={`Preview ${label}`}>
								<IconButton
									aria-label={`Preview ${label}`}
									color="primary"
									edge="end"
									size="small"
									onClick={() => setOpen(true)}
								>
									<VisibilityOutlinedIcon fontSize="small" />
								</IconButton>
							</Tooltip>
						</InputAdornment>
					) : null,
				}}
			/>
			{open && hasPersistedFile && (
				<FilePreviewModal
					label={label}
					fileId={fileId}
					fileName={fileName}
					onClose={() => setOpen(false)}
					onSaveRotated={onSaveRotated}
				/>
			)}
		</>
	);
};
